import { useCallback, useState } from 'react';

/**
 * Files on their way into a post, and what holds Save and Publish meanwhile.
 *
 * A file is not in the post until its upload lands: its slot is empty until then, so a Save or a
 * "Publish now" in that window goes out without it - and a Replace sends the OLD file. So while any
 * upload into the post is on its way - a gallery's, a lone video's, a PDF's, a thread card's, a design
 * imported from Canva or Figma - the composer holds Save, Publish now, Schedule and the queue, and
 * says why under each Save.
 *
 * Uploads are COUNTED, never flagged: with two on their way, the first to land must not release Save
 * while the second is still coming. And each is counted from its own start and end (its mutation's
 * `onMutate` and `onSettled`, which run whether or not the box that started it is still drawn), never
 * from a box being drawn or taken away. The editor is drawn on the Content tab only, and an upload
 * started there still lands after a switch to Schedule - so the composer keeps the count, and a box
 * taken away mid-upload holds Save until its request is answered (its file then lands nowhere).
 */

/** Said under each Save while an upload is on its way, and what a save made in code refuses with. */
export const UPLOAD_HOLD_MESSAGE =
  'Uploading… Save and Publish will be available as soon as it finishes.';

/** Uploads on their way, counted by the key of the first block of the box each one goes into. */
export type UploadCounts = ReadonlyMap<string, number>;

/**
 * `counts` after one upload started (`+1`) or ended (`-1`) under `anchorKey`. A key whose count
 * reaches zero is dropped, and an end with no start counts nothing - so the count never goes below
 * zero, and an empty map is exactly "nothing on its way".
 */
export function countUpload(
  counts: UploadCounts,
  anchorKey: string,
  delta: 1 | -1,
): UploadCounts {
  const next = new Map(counts);
  const count = (next.get(anchorKey) ?? 0) + delta;
  if (count > 0) next.set(anchorKey, count);
  else next.delete(anchorKey);
  return next;
}

/** Whether any upload into the post is still on its way. */
export function isUploading(counts: UploadCounts): boolean {
  return counts.size > 0;
}

/**
 * The count, kept in state, and how an upload reports its start and its end. `report` is the same
 * function on every render: an upload's end is reported from the closure it started with.
 */
export function useUploadCounts(): {
  counts: UploadCounts;
  report: (anchorKey: string, delta: 1 | -1) => void;
} {
  const [counts, setCounts] = useState<UploadCounts>(() => new Map());
  const report = useCallback(
    (anchorKey: string, delta: 1 | -1) =>
      setCounts((prev) => countUpload(prev, anchorKey, delta)),
    [],
  );
  return { counts, report };
}
